/**
 * Right-side slide-over drawer that loads & renders a KB article in
 * read-only mode without taking the user away from the current page.
 *
 * Logs a "view" use-event on first open (when `ticketId` is provided) so
 * the analytics dashboard in Phase 5 can attribute KB engagement to a ticket.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { BookOpen, Clock, Eye, Tag, ExternalLink, Link2 } from "lucide-react";
import { toast } from "sonner";
import KbVoteButtons from "@/components/kb/KbVoteButtons";

export default function KbArticleDrawer({
  articleId,
  open,
  onOpenChange,
  ticketId,
  onInsertLink,
}) {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load on open
  useEffect(() => {
    if (!open || !articleId) return;
    setLoading(true);
    api.get(`/kb/${articleId}`)
      .then((r) => setArticle(r.data))
      .catch(() => toast.error("Failed to load article"))
      .finally(() => setLoading(false));
    // Log view (best-effort)
    api.post(`/kb/${articleId}/log-use`, { action: "view", ticket_id: ticketId || null })
      .catch(() => {/* silent */});
  }, [open, articleId, ticketId]);

  // Rewrite KB image src with the user's auth token
  const html = useMemo(() => {
    const raw = article?.body_md || article?.content || "";
    if (!raw) return "";
    const tk = localStorage.getItem("kippu_token") || "";
    return raw.replace(
      /<img\s+([^>]*?)src=["']\/api\/kb-files\/([\w-]+)["']/g,
      (_m, attrs, fid) => `<img ${attrs}src="/api/kb-files/${fid}?token=${encodeURIComponent(tk)}"`,
    );
  }, [article]);

  const insertLink = async () => {
    if (!article || !onInsertLink) return;
    const md = `[${article.title}](/knowledge-base/${article.id})`;
    onInsertLink(md);
    // Log insert
    try {
      await api.post(`/kb/${article.id}/log-use`, { action: "insert", ticket_id: ticketId || null });
    } catch { /* silent */ }
    toast.success("Inserted into reply");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="!w-[760px] !max-w-[95vw] p-0 overflow-y-auto"
        data-testid="kb-article-drawer"
      >
        <SheetHeader className="px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <SheetTitle className="font-heading flex items-center gap-2 text-base">
            <BookOpen className="w-4 h-4 text-indigo-600" />
            <span className="truncate">{article?.title || "Loading…"}</span>
          </SheetTitle>
        </SheetHeader>
        <div className="px-6 pb-6 pt-4 space-y-4">
          {loading && <div className="text-sm text-slate-400 py-12 text-center">Loading article…</div>}
          {!loading && article && (
            <>
              <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
                {article.category && (
                  <span className="inline-flex items-center gap-1">
                    <BookOpen className="w-3 h-3" /> {article.category}
                  </span>
                )}
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Updated {article.updated_at ? new Date(article.updated_at).toLocaleDateString() : "—"}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Eye className="w-3 h-3" /> {article.view_count || 0} views
                </span>
                {(article.tags || []).length > 0 && (
                  <span className="inline-flex items-center gap-1 flex-wrap">
                    <Tag className="w-3 h-3" />
                    {(article.tags || []).slice(0, 6).map((t) => (
                      <Badge key={t} variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px]">{t}</Badge>
                    ))}
                  </span>
                )}
              </div>

              {article.summary && (
                <div className="rounded-md bg-slate-50 border border-slate-200 px-4 py-2 text-sm text-slate-700">
                  {article.summary}
                </div>
              )}

              <div
                className="prose prose-sm max-w-none kb-article-body"
                dangerouslySetInnerHTML={{ __html: html }}
                data-testid="kb-drawer-body"
              />

              <KbVoteButtons
                articleId={article.id}
                initialHelpful={article.helpful_count || 0}
                initialUnhelpful={article.unhelpful_count || 0}
              />

              <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                {onInsertLink && (
                  <Button onClick={insertLink} className="bg-indigo-600 hover:bg-indigo-700"
                          data-testid="kb-drawer-insert-link-btn">
                    <Link2 className="w-3.5 h-3.5 mr-1.5" /> Insert link in reply
                  </Button>
                )}
                <Link to={`/knowledge-base/${article.id}`} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" data-testid="kb-drawer-open-full-btn">
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open full page
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
